import React, { useState, useEffect } from 'react';
import { Modal } from '@mui/material';
import { useSnackbar } from 'notistack';
import BrowseAffiliates from 'components/Affiliates/BrowseAffiliates';
import CreateAffiliate from 'components/Affiliates/CreateAffiliate';
import UploadFile from 'components/Affiliates/UploadFile';
import AddAffiliate from 'components/Affiliates/AddAffiliate';
import EditAffiliate from 'components/Affiliates/EditAffiliate';
import AffiliatesAPI from 'api/AffiliatesAPI';
import AffiliatesTable from 'components/Affiliates/AffiliatesTable';
import AffiliatesPending from 'components/Affiliates/AffiliatesPending';
import AffiliatesDocuments from 'components/Affiliates/AffiliatesDocuments';
import { toBase64 } from 'utils/fileHelper';

interface IAffiliations {
  cur_user: any;
}

const Affiliations: React.FC<IAffiliations> = ({ cur_user }) => {
  const [ browseMode, setBrowseMode ] = useState(false);
  const [ createMode, setCreateMode ] = useState(false);
  const [ uploadMode, setUploadMode ] = useState(false);
  const [ addMode, setAddMode ] = useState(false);
  const [ editMode, setEditMode ] = useState(false);
  const [ search, setSearch ] = useState('');
  const [ tab, setTab ] = useState('members');
  const [ affiliate, setAffiliate ] = useState<any>({ isLoading: true, data: {} });
  const { enqueueSnackbar } = useSnackbar();

  const handleTabSwitch = (type: string) => {
    switch (type) {
    case 'members':
      setTab('members');
      break;
    case 'following':
      setTab('following');
      break;
    default:
      setTab('members');
    }
  };

  const getCurAffiliate = async() => {
    const mongoReq = {
      query: {
        kc_team_id: cur_user.selected_team?.id,
      },
    };
    const res = await AffiliatesAPI.retrieveAffiliate(mongoReq);
    if (res?.success) {
      setAffiliate({ isLoading: false, data: res?.result[0] });
    }
  };

  const createAffiliate = async(file: any) => {
    let logo = {};
    const newAffiliate: any = {
      kc_team_id: cur_user.selected_team.id,
      name: cur_user.selected_team.name,
      admin: cur_user.selected_team.attributes.team_admin[0],
    };
    if (file.path) {
      const base64Str = await toBase64(file);
      logo = {
        base64: base64Str,
        name: file?.path,
        dateAdded: Date.now(),
      };
      newAffiliate.logo = logo;
    }
    const res = await AffiliatesAPI.createAffiliate({
      newAffiliate: newAffiliate
    });
    if (res?.success) {
      setAffiliate({ isLoading: true, data: {} });
      setCreateMode(false);
      enqueueSnackbar('Affiliation created!', { variant: 'info' });
      getCurAffiliate();
    }
  };

  const handleInvite = async(type: string, index: number, item: any) => {
    const pendingUpdate = affiliate.data.pending;
    if (index > -1) {
      pendingUpdate.splice(index, 1);
    }
    if (type === 'approve') {
      if (item.type === 'incoming') {
        const res = await AffiliatesAPI.getAffiliateById(item.aff_id);
        if (res?.success) {
          const affiliateToUpdate = res?.result;
          const membersUpdate = affiliate.data.members;
          const followingUpdate = affiliateToUpdate.following;
          membersUpdate.push({
            ...item,
            dateJoined: Date.now()
          });
          followingUpdate.push({
            aff_id: affiliate.data._id,
            admin: affiliate.data.admin,
            name: affiliate.data.name,
            dateJoined: Date.now()
          });
          const res1 = await AffiliatesAPI.updateAffiliate({
            _id: affiliate.data._id,
            pending: pendingUpdate,
            members: membersUpdate
          });
          if (res1?.success) {
            const res2 = await AffiliatesAPI.updateAffiliate({
              _id: item.aff_id,
              following: followingUpdate
            });
            if (res2?.success) {
              setAffiliate({ isLoading: true, data: {} });
              getCurAffiliate();
            }
          }
        }
      }
      if (item.type === 'external') {
        const res = await AffiliatesAPI.getAffiliateById(item.aff_id);
        if (res?.success) {
          const affiliateToUpdate = res?.result;
          const membersUpdate = affiliateToUpdate.members;
          const followingUpdate = affiliate.data.following;
          membersUpdate.push({
            aff_id: affiliate.data._id,
            admin: affiliate.data.admin,
            name: affiliate.data.name,
            dateJoined: Date.now()
          });
          followingUpdate.push({
            ...item,
            dateJoined: Date.now()
          });
          const res1 = await AffiliatesAPI.updateAffiliate({
            _id: affiliate.data._id,
            pending: pendingUpdate,
            following: followingUpdate
          });
          if (res1?.success) {
            const res2 = await AffiliatesAPI.updateAffiliate({
              _id: item.aff_id,
              members: membersUpdate
            });
            if (res2?.success) {
              setAffiliate({ isLoading: true, data: {} });
              getCurAffiliate();
            }
          }
        }
      }
    }
    if (type === 'deny') {
      if (item.type === 'incoming') {
        const res = await AffiliatesAPI.updateAffiliate({
          _id: affiliate.data._id,
          pending: pendingUpdate,
        });
        if (res?.success) {
          setAffiliate({ isLoading: true, data: {} });
          getCurAffiliate();
        }
      }
      if (item.type === 'external') {
        const res = await AffiliatesAPI.updateAffiliate({
          _id: affiliate.data._id,
          pending: pendingUpdate,
        });
        if (res?.success) {
          setAffiliate({ isLoading: true, data: {} });
          getCurAffiliate();
        }
      }
    }
  };

  const removeMember = async(data: any, memberToRemove: any, index: number) => {
    const updatedMembers = data.members;
    const res = await AffiliatesAPI.getAffiliateById(memberToRemove.aff_id);
    if (res?.success) {
      const removedMember = res?.result;
      if (index > -1) {
        updatedMembers.splice(index, 1);
      }
      const res1 = await AffiliatesAPI.updateAffiliate({
        _id: affiliate.data._id,
        members: updatedMembers
      });
      if (res1?.success) {
        const filteredFollowing = removedMember.following.filter((follow: any) => follow.name !== data.name);
        const res2 = await AffiliatesAPI.updateAffiliate({
          _id: memberToRemove.aff_id,
          following: filteredFollowing
        });
        if (res2?.success) {
          setAffiliate({ isLoading: true, data: {} });
          getCurAffiliate();
        }
      }
    }
  };

  const unfollowAffiliate = async(data: any, followingToRemove: any, index: number) => {
    const affiliateToUpdate = data;
    const res = await AffiliatesAPI.getAffiliateById(followingToRemove.aff_id);
    if (res?.success) {
      const unfollowedAffiliate = res?.result;
      if (index > -1) {
        affiliateToUpdate.following.splice(index, 1);
      }
      const res1 = await AffiliatesAPI.updateAffiliate({
        _id: affiliateToUpdate._id,
        following: affiliateToUpdate.following
      });
      if (res1?.success) {
        const filteredMembers = unfollowedAffiliate.members.filter((member: any) => member.name !== affiliateToUpdate.name);
        const res2 = await AffiliatesAPI.updateAffiliate({
          _id: unfollowedAffiliate._id,
          members: filteredMembers
        });
        if (res2?.success) {
          setAffiliate({ isLoading: true, data: {} });
          getCurAffiliate();
        }
      }
    }
  };

  const deleteDoc = async(doc: any, index: number) => {
    const docs = affiliate.data.documents;
    if (index > -1) {
      docs.splice(index, 1);
    }
    const res = await AffiliatesAPI.updateAffiliate({
      _id: affiliate.data._id,
      documents: docs
    });
    if (res?.success) {
      setAffiliate({ isLoading: true, data: {} });
      getCurAffiliate();
    }
  };

  useEffect(() => {
    getCurAffiliate();
  }, []);

  return (
    <div className='affiliations-container'>
      {!affiliate.isLoading && affiliate.data === undefined && (
        <div className='create-affiliate'>
          <div className='create-affiliate-frame'>
            <div className='create-affiliate-group'>
              <div className='create-affiliate-group-primary'>
                Make this team an affiliate group
              </div>
              <div className='create-affiliate-group-secondary'>
                This allows your team to become an organization that can share
                documents and templates with other teams. They can then access,
                fill out, then screen these documents on their own. Your affiliate
                group name is the same as your team name.
              </div>
            </div>
            <div className='create-affiliate-btn' onClick={() => setCreateMode(true)}>
              <div className='create-affiliate-btn-text'>
                Create
              </div>
            </div>
          </div>
        </div>
      )}
      {!affiliate.isLoading && affiliate.data && (
        <>
          <AffiliatesTable
            affiliate={affiliate}
            tab={tab}
            setSearch={setSearch}
            setBrowseMode={setBrowseMode}
            setAddMode={setAddMode}
            setEditMode={setEditMode}
            handleTabSwitch={handleTabSwitch}
            removeMember={removeMember}
            unfollowAffiliate={unfollowAffiliate}
          />
          <AffiliatesPending
            affiliate={affiliate}
            handleInvite={handleInvite}
          />
          <AffiliatesDocuments
            affiliate={affiliate}
            setUploadMode={setUploadMode}
            deleteDoc={deleteDoc}
          />
        </>
      )}
      <Modal
        open={browseMode}
        onClose={() => setBrowseMode(false)}
      >
        <BrowseAffiliates cur_user={cur_user} affiliate={affiliate} setBrowseMode={setBrowseMode} />
      </Modal>
      <Modal
        open={createMode}
        onClose={() => setCreateMode(false)}
      >
        <CreateAffiliate createAffiliate={createAffiliate} setCreateMode={setCreateMode} cur_user={cur_user} />
      </Modal>
      <Modal
        open={uploadMode}
        onClose={() => setUploadMode(false)}
      >
        <UploadFile affiliate={affiliate} setUploadMode={setUploadMode} />
      </Modal>
      <Modal
        open={addMode}
        onClose={() => setAddMode(false)}
      >
        <AddAffiliate
          setAddMode={setAddMode}
          getCurAffiliate={getCurAffiliate}
          setAffiliate={setAffiliate}
          affiliate={affiliate}
        />
      </Modal>
      <Modal
        open={editMode}
        onClose={() => setEditMode(false)}
      >
        <EditAffiliate
          cur_user={cur_user}
          setEditMode={setEditMode}
          affiliate={affiliate}
          setAffiliate={setAffiliate}
          getCurAffiliate={getCurAffiliate}
        />
      </Modal>
    </div>
  );
};

export default Affiliations;
